import * as React from 'react';
import { TCategoriesMenu } from './CategoriesMenu.types';
import { PortalToID } from '../../../../../utils/dom';
import { CategoryRow } from './components/CategoryRow/CategoryRow';
import { Subcategory } from './components/Subcategory/Subcategory';
import './CategoriesMenu.styled.scss';
import classNames from 'classnames';
import { CATEGORIES_MENU_CONFIG } from './config';
import { IconButton } from 'components';
import MenuIcon from '@mui/icons-material/Menu';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation } from 'react-router';
import { APP_ROUTES } from '../../../../../constants/routes';
import { useFilters } from '../../../../../store/contexts/FiltersContext/FiltersContext.provider';
import { useDeviceType } from 'hooks/useDeviceType';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';

export const CategoriesMenu: TCategoriesMenu = () => {
  const MENU_CLOSE_ANIMATION_MS = 220;
  const MENU_OPEN_ANIMATION_DELAY_MS = 10;
  const localize = useLocalize();
  const location = useLocation();
  const { isMobile, isTablet } = useDeviceType();
  const { openFilters } = useFilters();
  const [isCategoriesMenuOpen, setCategoriesMenuOpen] = React.useState(false);
  const [isClosingMenu, setIsClosingMenu] = React.useState(false);
  const [isOpeningMenu, setIsOpeningMenu] = React.useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = React.useState<
    string | null
  >(null);
  const [hasPointerEnteredMenu, setHasPointerEnteredMenu] =
    React.useState(false);

  const menuRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLDivElement>(null);
  const openTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const openAnimationRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const isOnProductsPage = location.pathname === APP_ROUTES.PHONE_CASES;

  const closeMenu = React.useCallback(() => {
    if (!isCategoriesMenuOpen || isClosingMenu) return;

    setIsOpeningMenu(false);

    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    if (openAnimationRef.current) {
      clearTimeout(openAnimationRef.current);
      openAnimationRef.current = null;
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    setIsClosingMenu(true);
    closeTimeoutRef.current = setTimeout(() => {
      setCategoriesMenuOpen(false);
      setIsClosingMenu(false);
      setSelectedCategoryId(null);
      setHasPointerEnteredMenu(false);
      closeTimeoutRef.current = null;
    }, MENU_CLOSE_ANIMATION_MS);
  }, [isCategoriesMenuOpen, isClosingMenu]);

  const handleCategoriesToggle = () => {
    // On mobile and products page, open filters instead
    if (isMobile && isOnProductsPage) {
      openFilters();
      return;
    }

    const willOpen = !isCategoriesMenuOpen;
    if (!willOpen) {
      closeMenu();
      return;
    }

    setCategoriesMenuOpen(true);
    setIsClosingMenu(false);
    setIsOpeningMenu(true);

    if (openAnimationRef.current) {
      clearTimeout(openAnimationRef.current);
      openAnimationRef.current = null;
    }
    openAnimationRef.current = setTimeout(() => {
      setIsOpeningMenu(false);
      openAnimationRef.current = null;
    }, MENU_OPEN_ANIMATION_DELAY_MS);

    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }

    if (willOpen) {
      setSelectedCategoryId(null);
      setHasPointerEnteredMenu(false);

      // Desktop only: close menu if user doesn't move pointer into menu quickly.
      if (!isTablet) {
        openTimeoutRef.current = setTimeout(() => {
          closeMenu();
          openTimeoutRef.current = null;
        }, 3000);
      }
    }
  };

  const handleCategoryHover = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
  };

  const handleCategoryLeave = () => {
    // Don't clear immediately - let subcategories area handle it
  };

  const handleMenuEnter = () => {
    setHasPointerEnteredMenu(true);
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
  };

  const handleMenuLeave = () => {
    // If user entered menu and then leaves, close it.
    if (hasPointerEnteredMenu) {
      closeMenu();
    }
    // Clear selection when leaving the entire menu
    setSelectedCategoryId(null);
  };

  const handleCategoryClick = React.useCallback(() => {
    closeMenu();
  }, [closeMenu]);

  // Handle click outside to close menu
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        closeMenu();
      }
    };

    if (isCategoriesMenuOpen) {
      // Use a small delay to avoid immediate closure when opening
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCategoriesMenuOpen, closeMenu]);

  React.useEffect(() => {
    return () => {
      if (openTimeoutRef.current) {
        clearTimeout(openTimeoutRef.current);
      }
      if (openAnimationRef.current) {
        clearTimeout(openAnimationRef.current);
      }
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const selectedCategory = selectedCategoryId
    ? CATEGORIES_MENU_CONFIG.find((cat) => cat.id === selectedCategoryId)
    : null;

  return (
    <div>
      {isMobile || isTablet ? (
        <>
          <IconButton
            ref={buttonRef as unknown as React.RefObject<HTMLButtonElement>}
            onClick={handleCategoriesToggle}
            className="categories-menu__hamburger"
            color="primary"
          >
            <MenuIcon fontSize="large" />
          </IconButton>
          {(isCategoriesMenuOpen || isClosingMenu) && (
            <PortalToID id="app_header">
              <div
                className={classNames(
                  'categories-menu',
                  'categories-menu--mobile',
                  {
                    'categories-menu--open':
                      isCategoriesMenuOpen && !isClosingMenu && !isOpeningMenu,
                    'categories-menu--closing': isClosingMenu,
                  }
                )}
                ref={menuRef}
              >
                <div className="categories-menu__mobile-content">
                  {CATEGORIES_MENU_CONFIG.map((category) => (
                    <Accordion
                      key={category.id}
                      className="categories-menu__accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        className="categories-menu__accordion-summary"
                      >
                        {localize(category.nameKey)}
                      </AccordionSummary>
                      <AccordionDetails className="categories-menu__accordion-details">
                        {category.subCategories && (
                          <Subcategory
                            subcategories={category.subCategories}
                            categoryId={category.id}
                            onCategoryClick={handleCategoryClick}
                          />
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </div>
              </div>
            </PortalToID>
          )}
        </>
      ) : (
        <>
          <div
            ref={buttonRef}
            onClick={handleCategoriesToggle}
            className={classNames(
              'caption',
              'header__main-content__links__link',
              'categories-menu__categories-button'
            )}
          >
            {localize('header.categories.button')}
          </div>

          {(isCategoriesMenuOpen || isClosingMenu) && (
            <PortalToID id="app_header">
              <div
                className={classNames('categories-menu', {
                  'categories-menu--open':
                    isCategoriesMenuOpen && !isClosingMenu && !isOpeningMenu,
                  'categories-menu--closing': isClosingMenu,
                })}
                ref={menuRef}
                onMouseEnter={handleMenuEnter}
                onMouseLeave={handleMenuLeave}
              >
                <div className="categories-menu__main-categories">
                  {CATEGORIES_MENU_CONFIG.map((category) => (
                    <CategoryRow
                      key={category.id}
                      category={category}
                      isSelected={selectedCategoryId === category.id}
                      onMouseEnter={() => handleCategoryHover(category.id)}
                      onMouseLeave={handleCategoryLeave}
                      onCategoryClick={handleCategoryClick}
                    />
                  ))}
                </div>
                {selectedCategoryId && selectedCategory && (
                  <div
                    className={classNames('categories-menu__subcategories', {
                      'categories-menu__subcategories--visible':
                        selectedCategoryId !== null,
                    })}
                    onMouseEnter={() =>
                      handleCategoryHover(selectedCategory.id)
                    }
                    onMouseLeave={handleCategoryLeave}
                  >
                    <Subcategory
                      subcategories={selectedCategory.subCategories}
                      categoryId={selectedCategory.id}
                      onCategoryClick={handleCategoryClick}
                    />
                  </div>
                )}
              </div>
            </PortalToID>
          )}
        </>
      )}
    </div>
  );
};
